<template>
  <div :class="{ 'modal-backdrop': !isEmbedded }" @click.self="!isEmbedded && close()">
    <div :class="{ 'modal-content': !isEmbedded }">
      <header v-if="!isEmbedded" class="modal-header">
        <h2>{{ isEditMode ? 'Edit Appointment' : 'Create New Appointment' }}</h2>
        <button @click="close" class="btn-close">&times;</button>
      </header>
      <section :class="{ 'modal-body': !isEmbedded }">
        <div v-if="readOnly" class="readonly-banner">
          <template v-if="appointment?.status === 'completed'">
            Payment has been recorded. This appointment is completed and cannot be edited.
          </template>
          <template v-else>
            This appointment cannot be edited.
          </template>
        </div>
        <form @submit.prevent="submitForm" class="booking-form">
          <!-- Internal Booking Fields -->
          <div class="form-row">
            <div class="form-group">
              <label for="customer">Customer</label>
              <input type="text" id="customer" :value="`${customer?.firstName} ${customer?.lastName}`" disabled />
            </div>
          </div>

          <div class="form-row" v-if="isEditMode && isStaffProps">
            <div class="form-group">
              <label for="status">Status</label>
              <select id="status" v-model="form.status" :disabled="readOnly">
                <option v-for="s in editableStatuses" :key="s" :value="s">
                  {{ statusLabels[s] }}
                </option>
              </select>
              <small class="hint-text">To complete, record payment below — status will update automatically.</small>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="appointmentDate">Date</label>
              <input type="date" id="appointmentDate" v-model="form.appointmentDate" required :disabled="readOnly" />
            </div>
            <div class="form-group">
              <label for="appointmentTime">Time</label>
              <input type="time" id="appointmentTime" v-model="form.time" required @change="validateTime" :disabled="readOnly" />
              <small v-if="timeError" class="error-text">{{ timeError }}</small>
            </div>
          </div>

          <div class="services-section">
            <h3>Services</h3>
            <div v-for="(service, index) in form.services" :key="index" class="service-item">
              <div class="form-row">
                <div class="form-group">
                  <label>Service</label>
                  <select v-model="service.serviceId" @change="updateServicePrice(index)" required :disabled="readOnly">
                    <option disabled value="">Select a service</option>
                    <option v-for="s in (availableServices as Service[])" :key="s.id" :value="s.id">
                      {{ s.name }}
                    </option>
                  </select>
                </div>
                <div v-if="isStaffProps" class="form-group">
                  <label>Employee</label>
                  <select v-model="service.employeeId" :disabled="readOnly">
                    <option value="">Any Available</option>
                    <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                      {{ employee.firstName }} {{ employee.lastName }}
                    </option>
                  </select>
                </div>
                <div v-if="isStaffProps" class="form-group">
                  <label>Price (₹)</label>
                  <input
                    type="number"
                    v-model.number="service.price"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    :disabled="!service.serviceId || readOnly"
                  />
                </div>
              </div>
              <button v-if="!readOnly" type="button" @click="removeService(index)" class="btn-remove-service">Remove</button>
            </div>
            <button v-if="!readOnly" type="button" @click="addService" class="btn btn-secondary">Add Service</button>
          </div>

          <div v-if="isStaffProps" class="form-group">
            <label for="offer">Offer (Optional)</label>
            <select id="offer" v-model="form.offerId" :disabled="readOnly || !!form.spinVoucherCode" @change="onOfferChange">
              <option :value="null">No offer</option>
              <option v-for="offer in activeOffers" :key="offer.id" :value="offer.id">
                {{ formatOfferLabel(offer) }}
              </option>
              <option
                v-if="voucherOffer && !activeOffers.some(o => o.id === voucherOffer.id)"
                :value="voucherOffer.id"
              >
                {{ formatOfferLabel(voucherOffer) }} (spin voucher)
              </option>
            </select>
          </div>

          <div v-if="isStaffProps" class="form-group">
            <label for="spinVoucherCode">Spin voucher code (Optional)</label>
            <div class="voucher-row">
              <input
                id="spinVoucherCode"
                v-model="form.spinVoucherCode"
                type="text"
                placeholder="e.g. DIWALI-ABC123"
                :disabled="readOnly"
                @keyup.enter.prevent="applySpinVoucher"
              />
              <button
                v-if="!readOnly"
                type="button"
                class="btn btn-secondary btn-apply-voucher"
                :disabled="!form.spinVoucherCode.trim() || voucherLoading"
                @click="applySpinVoucher"
              >
                {{ voucherLoading ? 'Checking…' : 'Apply' }}
              </button>
              <button
                v-if="!readOnly && form.spinVoucherCode"
                type="button"
                class="btn btn-secondary"
                @click="clearSpinVoucher"
              >
                Clear
              </button>
            </div>
            <small v-if="voucherMessage" class="hint-text" :class="{ 'voucher-error': voucherError }">
              {{ voucherMessage }}
            </small>
            <small v-else class="hint-text">
              Enter the customer’s spin code to apply their prize. Cannot combine with another offer.
            </small>
          </div>

          <div class="form-group">
            <label for="notes">Notes (Optional)</label>
            <textarea id="notes" v-model="form.notes" rows="3" :disabled="readOnly"></textarea>
          </div>

          <div v-if="isStaffProps" class="payment-section">
            <h3>Billing &amp; Payment</h3>
            <div class="price-summary">
              <div class="price-row">
                <span>Total Bill (services)</span>
                <strong>₹{{ subtotal.toFixed(2) }}</strong>
              </div>
              <div v-if="discountAmount > 0" class="price-row discount">
                <span>Discount</span>
                <strong>- ₹{{ discountAmount.toFixed(2) }}</strong>
              </div>
              <div class="price-row highlight">
                <span>Amount After Discount</span>
                <strong>₹{{ amountAfterDiscount.toFixed(2) }}</strong>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="amountReceived">Amount Received (₹)</label>
                <input
                  id="amountReceived"
                  type="number"
                  v-model.number="form.amountReceived"
                  min="0"
                  step="0.01"
                  placeholder="Actual amount paid"
                  :disabled="readOnly"
                />
                <small class="hint-text">Enter the amount the customer paid. Saving will mark this appointment as <strong>completed</strong> and lock further edits.</small>
              </div>
              <div class="form-group">
                <label for="tips">Tips (₹)</label>
                <input
                  id="tips"
                  type="number"
                  v-model.number="form.tips"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  :disabled="readOnly"
                />
              </div>
            </div>

            <div
              v-if="paymentAdjustment != null && paymentAdjustment !== 0"
              class="price-row adjustment"
              :class="adjustmentClass"
            >
              <span>{{ adjustmentLabel }}</span>
              <strong>₹{{ adjustmentDisplayAmount }}</strong>
            </div>
            <div v-if="form.tips > 0" class="price-row tips-row">
              <span>Total Collected (received + tips)</span>
              <strong>₹{{ totalCollected.toFixed(2) }}</strong>
            </div>
          </div>

          <div class="total-bill" v-else-if="showPrice">
            <strong>Total: ₹{{ amountAfterDiscount.toFixed(2) }}</strong>
          </div>

          <footer v-if="!readOnly" class="form-footer" :class="{ 'modal-footer': !isEmbedded }">
            <button type="button" class="btn btn-secondary" @click="close">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              {{ submitButtonLabel }}
            </button>
          </footer>
        </form>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch, PropType } from 'vue';
import { useCustomerStore } from '../store/customer.store';
import { useServiceStore } from '../store/services.store';
import { useUserStore } from '../store/user.store';
import {
  IAppointment,
  AppointmentStatus,
  APPOINTMENT_STATUSES,
  STATUS_LABELS,
} from '../types/appointment.types';
import { Service } from '../types/service.types';
import { Customer } from '@/types/customer.types';
import offerService from '@/services/offer.service';
import spinPlayService from '@/services/spinPlay.service';
import type { Offer } from '@/types/offer.types';
import {
  calculateOfferDiscount,
  calculateAmountAfterDiscount,
  calculatePaymentAdjustment,
  formatOfferLabel,
} from '@/utils/offerPricing';
import { useConfirm } from '@/composables/useConfirm';
import { getApiErrorMessage } from '@/utils/apiError';
import { Status } from '@/types/common.types';

interface ServiceLineItem {
  serviceId?: string;
  employeeId?: string;
  price?: number;
}

export default defineComponent({
  name: 'AppointmentForm',
  props: {
    appointment: {
      type: Object as PropType<IAppointment | null>,
      default: null,
    },
    customer: {
      type: Object as PropType<Customer | null>,
      default: null,
    },
    isEmbedded: {
      type: Boolean,
      default: false,
    },
    showPrice: {
      type: Boolean,
      default: true,
    },
    isStaff: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'cancel', 'submit-success'],
  setup(props, { emit }) {
    const customerStore = useCustomerStore();
    const serviceStore = useServiceStore();
    const userStore = useUserStore();
    const { confirm } = useConfirm();

    const form = ref<{
      clientId: string;
      appointmentDate: string;
      time: string;
      services: ServiceLineItem[];
      notes: string;
      status: AppointmentStatus;
      offerId: string | null;
      spinVoucherCode: string;
      amountReceived: number | null;
      tips: number;
    }>({
      clientId: '',
      appointmentDate: new Date().toISOString().split('T')[0],
      time: '',
      services: [],
      notes: '',
      status: 'booked' as AppointmentStatus,
      offerId: null,
      spinVoucherCode: '',
      amountReceived: null,
      tips: 0,
    });

    const activeOffers = ref<Offer[]>([]);
    const voucherOffer = ref<Offer | null>(null);
    const voucherLoading = ref(false);
    const voucherMessage = ref('');
    const voucherError = ref(false);

    const timeError = ref('');
    const statusLabels = STATUS_LABELS;
    const editableStatuses = APPOINTMENT_STATUSES.filter(s => s !== 'completed');

    const isSubmitting = ref(false);
    const isEditMode = computed(() => !!props.appointment);

    watch(() => props.appointment, (newAppointment) => {
      if (newAppointment) {
        const appointmentDateTime = new Date(newAppointment.appointment_date);
        form.value = {
          ...form.value,
          clientId: newAppointment.customerId,
          appointmentDate: appointmentDateTime.toISOString().split('T')[0],
          time: appointmentDateTime.toTimeString().split(' ')[0].substring(0, 5),
          services: (newAppointment.services ?? []).map(s => ({
            serviceId: s.serviceId || (s as any).service_id,
            employeeId: s.employeeId,
            price: s.priceAtBooking != null
              ? Number(s.priceAtBooking)
              : s.service?.price != null
                ? Number(s.service.price)
                : undefined,
          })),
          notes: newAppointment.notes || '',
          status: newAppointment.status as AppointmentStatus,
          offerId: newAppointment.offerId ?? null,
          spinVoucherCode: '',
          amountReceived: newAppointment.amountReceived != null ? Number(newAppointment.amountReceived) : null,
          tips: newAppointment.tips != null ? Number(newAppointment.tips) : 0,
        };
      } else {
        form.value = {
          ...form.value,
          clientId: props.customer ? props.customer.id : '',
          appointmentDate: new Date().toISOString().split('T')[0],
          time: '',
          services: [],
          notes: '',
          status: 'booked',
          offerId: null,
          spinVoucherCode: '',
          amountReceived: null,
          tips: 0,
        };
      }
      voucherOffer.value = null;
      voucherMessage.value = '';
      voucherError.value = false;
    }, { immediate: true, deep: true });

    watch(() => props.customer, (newCustomer) => {
      if (newCustomer) {
        form.value.clientId = newCustomer.id;
      }
    });

    const addService = () => {
      form.value.services.push({
        serviceId: undefined,
        employeeId: undefined,
      });
    };

    const removeService = (index: number) => {
      form.value.services.splice(index, 1);
    };

    const updateServicePrice = (index: number) => {
      const serviceId = form.value.services[index].serviceId;
      const service = serviceStore.services.find(s => s.id === serviceId);
      if (service) {
        form.value.services[index].price = Number(service.price);
      }
    };

    const servicesSubtotal = computed(() => {
      return form.value.services.reduce((total: number, service: ServiceLineItem) => {
        if (service.price != null && !Number.isNaN(Number(service.price))) {
          return total + Number(service.price);
        }
        const s = serviceStore.services.find(sv => sv.id === service.serviceId);
        return total + (s ? Number(s.price) : 0);
      }, 0);
    });

    const selectedOffer = computed(() => {
      if (voucherOffer.value && voucherOffer.value.id === form.value.offerId) {
        return voucherOffer.value;
      }
      return activeOffers.value.find(o => o.id === form.value.offerId) ?? null;
    });

    const subtotal = computed(() => {
      if (props.readOnly && props.appointment?.subtotal != null) {
        return Number(props.appointment.subtotal);
      }
      return servicesSubtotal.value;
    });

    const discountAmount = computed(() => {
      if (props.readOnly && props.appointment?.discountAmount != null) {
        return Number(props.appointment.discountAmount);
      }
      return calculateOfferDiscount(subtotal.value, selectedOffer.value);
    });

    const amountAfterDiscount = computed(() => {
      if (props.readOnly && props.appointment?.totalBill != null) {
        return Number(props.appointment.totalBill);
      }
      return calculateAmountAfterDiscount(subtotal.value, discountAmount.value);
    });

    const paymentAdjustment = computed(() => {
      if (props.readOnly && props.appointment?.paymentAdjustment != null) {
        return Number(props.appointment.paymentAdjustment);
      }
      return calculatePaymentAdjustment(amountAfterDiscount.value, form.value.amountReceived);
    });

    const adjustmentClass = computed(() => {
      const adj = paymentAdjustment.value;
      if (adj == null || adj === 0) return '';
      return adj > 0 ? 'adjustment-under' : 'adjustment-over';
    });

    const adjustmentLabel = computed(() => {
      const adj = paymentAdjustment.value;
      if (adj == null || adj === 0) return '';
      return adj > 0
        ? 'Short payment (customer paid less than due)'
        : 'Extra payment (customer paid more than due)';
    });

    const adjustmentDisplayAmount = computed(() => {
      const adj = paymentAdjustment.value;
      if (adj == null) return '0.00';
      return Math.abs(adj).toFixed(2);
    });

    const totalCollected = computed(() => {
      const received = form.value.amountReceived ?? 0;
      return received + (form.value.tips ?? 0);
    });

    const onOfferChange = () => {
      if (!form.value.spinVoucherCode) {
        voucherOffer.value = null;
        voucherMessage.value = '';
        voucherError.value = false;
      }
    };

    const clearSpinVoucher = () => {
      form.value.spinVoucherCode = '';
      voucherOffer.value = null;
      voucherMessage.value = '';
      voucherError.value = false;
      form.value.offerId = null;
    };

    const applySpinVoucher = async () => {
      const code = form.value.spinVoucherCode.trim();
      if (!code) return;

      voucherLoading.value = true;
      voucherMessage.value = '';
      voucherError.value = false;
      try {
        const preview = await spinPlayService.previewVoucher(code);
        if (!preview.canRedeem) {
          voucherError.value = true;
          voucherMessage.value = preview.reason || 'This voucher cannot be redeemed.';
          return;
        }

        const mappedOffer: Offer = {
          id: preview.offer.id,
          title: preview.offer.title,
          description: preview.segment?.label
            ? `Spin prize: ${preview.segment.label}`
            : preview.offer.title,
          discountPercentage: preview.offer.discountPercentage ?? undefined,
          discountAmount: preview.offer.discountAmount ?? undefined,
          validFrom: preview.validFrom,
          validUntil: preview.validUntil,
          status: Status.ACTIVE,
        };

        voucherOffer.value = mappedOffer;
        form.value.spinVoucherCode = preview.code;
        form.value.offerId = preview.offer.id;
        voucherMessage.value = `Applied ${preview.segment.label} from ${preview.campaign.name}. Valid until ${new Date(preview.validUntil).toLocaleDateString()}.`;
      } catch (err: unknown) {
        voucherError.value = true;
        voucherMessage.value = getApiErrorMessage(err, 'Could not apply spin voucher.');
        voucherOffer.value = null;
      } finally {
        voucherLoading.value = false;
      }
    };

    const willCompleteOnSubmit = computed(() => {
      if (props.readOnly) return false;
      const received = form.value.amountReceived;
      return received !== null && received !== undefined && !Number.isNaN(Number(received));
    });

    const submitButtonLabel = computed(() => {
      if (isSubmitting.value) return 'Saving...';
      if (willCompleteOnSubmit.value) {
        return isEditMode.value ? 'Record Payment & Complete' : 'Book & Complete';
      }
      return isEditMode.value ? 'Update Appointment' : 'Book Appointment';
    });

    const validateTime = () => {
      const time = form.value.time;
      if (time) {
        const [hours] = time.split(':').map(Number);
        if (hours < 10 || hours >= 21) {
          timeError.value = 'Appointments can only be booked between 10:00 AM and 9:00 PM.';
        } else {
          timeError.value = '';
        }
      }
    };

    const submitForm = async () => {
      validateTime();
      if (timeError.value) return;

      if (willCompleteOnSubmit.value) {
        const ok = await confirm({
          title: isEditMode.value ? 'Complete Appointment' : 'Create & Complete',
          message: isEditMode.value
            ? 'Recording payment will mark this appointment as completed and it cannot be edited later. Continue?'
            : 'This will create and immediately complete the appointment. Continue?',
          confirmText: 'Continue',
          cancelText: 'Cancel',
          variant: 'warning',
        });
        if (!ok) return;
      }

      isSubmitting.value = true;

      const appointmentDateTime = `${form.value.appointmentDate}T${form.value.time}:00`;
      const payload: Record<string, unknown> = {
        appointmentDate: appointmentDateTime,
        notes: form.value.notes,
        services: form.value.services
          .filter(s => s.serviceId)
          .map(s => ({
            serviceId: s.serviceId,
            employeeId: s.employeeId || undefined,
            priceOverride: s.price,
          })),
      };

      if (isEditMode.value) {
        payload.status = form.value.status;
        if (willCompleteOnSubmit.value) {
          delete payload.status;
        }
      } else {
        payload.clientId = form.value.clientId;
      }

      if (props.isStaff) {
        payload.offerId = form.value.offerId;
        if (form.value.spinVoucherCode.trim()) {
          payload.spinVoucherCode = form.value.spinVoucherCode.trim().toUpperCase();
        }
        if (willCompleteOnSubmit.value) {
          payload.amountReceived = form.value.amountReceived;
          payload.tips = form.value.tips ?? 0;
        } else if (isEditMode.value && form.value.tips > 0) {
          payload.tips = form.value.tips;
        }
      }

      emit('submit', payload);
      isSubmitting.value = false;
    };

    const close = () => {
      emit('cancel');
    };

    onMounted(async () => {
      await serviceStore.fetchServices({limit: 1000});
      if (props.isStaff) {
        await userStore.fetchUsers({ limit: 1000, status: 'active' });
        try {
          activeOffers.value = await offerService.getActiveOffers();
        } catch {
          activeOffers.value = [];
        }
      }
      if (props.isEmbedded && !isEditMode.value && form.value.services.length === 0) {
        addService();
      }
    });

    return {
      form,
      isStaffProps: props.isStaff,
      customers: computed(() => customerStore.customers),
      availableServices: computed(() => serviceStore.services),
      employees: computed(() => userStore.users),
      isEditMode,
      isSubmitting,
      addService,
      removeService,
      updateServicePrice,
      subtotal,
      discountAmount,
      amountAfterDiscount,
      paymentAdjustment,
      adjustmentClass,
      adjustmentLabel,
      adjustmentDisplayAmount,
      totalCollected,
      activeOffers,
      voucherOffer,
      voucherLoading,
      voucherMessage,
      voucherError,
      formatOfferLabel,
      onOfferChange,
      applySpinVoucher,
      clearSpinVoucher,
      submitForm,
      close,
      timeError,
      validateTime,
      editableStatuses,
      statusLabels,
      willCompleteOnSubmit,
      submitButtonLabel,
    };
  },
});
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  z-index: 1000;
  padding-top: 5rem;
}

.modal-content {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  width: 90%;
  max-width: 700px;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 25px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
}

.modal-body {
  padding: 25px;
  overflow-y: auto;
  max-height: 75vh;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.form-row .form-group {
  flex: 1 1 200px;
  margin-bottom: 0;
}

/* Standalone fields (not inside a row) */
.booking-form > .form-group {
  margin-bottom: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-group label {
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 0.9rem;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
}

.services-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.service-item {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  position: relative;
}

.btn-remove-service {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 5%;
  height: 24px;
  cursor: pointer;
  font-weight: bold;
}

.total-bill {
  margin-top: 20px;
  font-size: 1.2rem;
  font-weight: bold;
  text-align: right;
}

.price-breakdown {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.95rem;
  font-weight: normal;
  margin-bottom: 8px;
  color: #555;
}

.readonly-banner {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-weight: 500;
}

.payment-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.payment-section h3 {
  margin: 0 0 12px;
  font-size: 1.1rem;
}

.price-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 0.95rem;
}

.price-row.highlight {
  border-top: 1px solid #dee2e6;
  margin-top: 6px;
  padding-top: 10px;
  font-size: 1.05rem;
}

.price-row.discount strong {
  color: #16a34a;
}

.price-row.adjustment-under strong {
  color: #dc2626;
}

.price-row.adjustment-over strong {
  color: #16a34a;
}

.price-row.tips-row {
  border-top: 1px dashed #dee2e6;
  margin-top: 8px;
  padding-top: 8px;
}

.hint-text {
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 4px;
}

.voucher-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.voucher-row input {
  flex: 1 1 180px;
}

.btn-apply-voucher {
  white-space: nowrap;
}

.voucher-error {
  color: #b91c1c;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 15px 25px;
  border-top: 1px solid #e9ecef;
  margin-top: 20px;
}

.btn {
  padding: 10px 20px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: bold;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  margin-right: 10px;
}

.btn-primary {
  background-color: var(--brand-gold);
  color: var(--brand-black);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-text {
  color: #dc3545;
  font-size: 0.875em;
}

@media (max-width: 768px) {
  .booking-form .form-row {
    flex-direction: column;
    flex-wrap: nowrap;
    gap: 8px;
    margin-bottom: 8px;
  }

  .booking-form .form-row .form-group {
    flex: none;
    width: 100%;
  }

  .booking-form > .form-group {
    margin-bottom: 8px;
  }

  .services-section {
    margin-top: 10px;
    padding-top: 10px;
  }

  .services-section h3 {
    margin: 0 0 8px;
    font-size: 1.1rem;
  }

  .service-item {
    padding: 10px;
    margin-bottom: 8px;
  }

  .service-item .form-row {
    margin-bottom: 0;
    gap: 8px;
  }

  .total-bill {
    margin-top: 10px;
    font-size: 1.05rem;
  }

  .form-footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
    padding-top: 0;
    border-top: none;
  }

  .form-footer .btn {
    width: 100%;
    margin: 0 !important;
    min-height: 40px;
    padding: 8px 16px;
  }

  .modal-backdrop {
    padding: 0.5rem;
    padding-top: 4rem;
  }

  .modal-body {
    padding: 16px;
    max-height: 65vh;
  }

  .modal-footer {
    flex-direction: column-reverse;
    gap: 8px;
    margin-top: 10px;
    padding: 12px 16px;
  }

  .modal-footer .btn {
    width: 100%;
    margin-right: 0 !important;
    min-height: 40px;
  }

  .btn-remove-service {
    position: static;
    width: 100%;
    margin-top: 6px;
    border-radius: 4px;
    min-height: 36px;
  }

  .services-section > .btn-secondary {
    min-height: 40px;
    margin-top: 4px;
  }
}
</style>